// Tenantra Inc. - Reports Management Page (Microsoft 365 Admin Style)
import { useEffect, useState } from 'react';
import { 
  FileText, Plus, Search, Calendar, CheckCircle, AlertTriangle,
  Clock, XCircle, Home, User, ChevronDown, ChevronUp, Filter,
  Edit, LogOut, DollarSign, Hash, Building2, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { navigate } from '@/services/navigation';
import api, { apiHelpers } from '@/services/api';
import { useUrlParam } from '@/services/useUrlParam';
import SubscriptionGate from '@/components/SubscriptionGate';
import { TableSkeleton, SortableHeader, useSort, exportCsv, ExportButton, RowOpenButton } from '@/components/dashboard/TableKit';
import { TenantPanel } from '@/components/dashboard/TenantPanel';

// Status config
const STATUS_CONFIG = {
  active: { label: 'Active', dotColor: 'bg-emerald-500', textColor: 'text-emerald-600' },
  ended: { label: 'Ended', dotColor: 'bg-slate-400', textColor: 'text-slate-500' },
  pending_consent: { label: 'Pending', dotColor: 'bg-amber-500', textColor: 'text-amber-600' },
  terminated: { label: 'Terminated', dotColor: 'bg-red-500', textColor: 'text-red-600' }
};

// Expandable Tenancy Row Component
function TenancyRow({ tenancy, tenant, reports, onEndTenancy, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const statusConfig = STATUS_CONFIG[tenancy.status] || STATUS_CONFIG.active;
  
  const paymentSummary = reports.reduce((acc, r) => {
    if (r.payment?.status === 'on_time') acc.onTime++;
    else if (r.payment?.status === 'late') acc.late++;
    else if (r.payment?.status === 'missed') acc.missed++;
    else if (r.payment?.status === 'partial') acc.partial++;
    return acc;
  }, { onTime: 0, late: 0, missed: 0, partial: 0 });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const [year, month, day] = dateStr.split('T')[0].split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      {/* Main Row */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded(!expanded)}
        className="lens lens-row w-full grid grid-cols-12 gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors items-center text-left cursor-pointer"
        data-testid={`tenancy-row-${tenancy.id}`}
      >
        {/* Name with dot */}
        <div className="col-span-12 sm:col-span-3 flex items-center gap-2 min-w-0">
          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusConfig.dotColor}`} />
          <div className="min-w-0">
            <div className="text-xs font-medium text-slate-800 truncate">{tenant?.full_name || 'Unknown'}</div>
            <div className="text-[10px] text-slate-400 font-mono">{tenant?.tenant_unique_id || '------'}</div>
          </div>
        </div>

        {/* Property */}
        <div className="hidden sm:block col-span-2">
          <span className="text-xs text-slate-500 truncate block">{tenancy.property_address || 'No address'}</span>
        </div>

        {/* Rent */}
        <div className="hidden sm:block col-span-2">
          <span className="text-xs text-slate-500">${tenancy.rent_amount?.toLocaleString() || '—'}/mo</span>
        </div>

        {/* Date */}
        <div className="hidden sm:block col-span-2">
          <span className="text-xs text-slate-500">{formatDate(tenancy.move_in_date)}</span>
        </div>

        {/* Status */}
        <div className="hidden sm:flex col-span-2 items-center justify-between">
          <span className={`text-xs ${statusConfig.textColor}`}>{statusConfig.label}</span>
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-300" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-300" />}
        </div>

        <div className="hidden sm:flex col-span-1 justify-end">
          <RowOpenButton onClick={() => onOpen(tenancy.tenant_id)} testId={`tenancy-open-${tenancy.id}`} label={`Open ${tenant?.full_name || 'tenant'}`} />
        </div>

        {/* Mobile bottom */}
        <div className="sm:hidden col-span-12 flex items-center justify-between -mt-0.5">
          <span className="text-[10px] text-slate-400 truncate">{tenancy.property_address || ''}</span>
          <span className="flex items-center gap-2">
            <span className={`text-[10px] ${statusConfig.textColor}`}>{statusConfig.label}</span>
            <RowOpenButton onClick={() => onOpen(tenancy.tenant_id)} testId={`tenancy-open-mobile-${tenancy.id}`} label={`Open ${tenant?.full_name || 'tenant'}`} />
          </span>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 bg-slate-50/50">
          {/* Details Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-3 text-sm">
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Property</p>
              <p className="text-slate-700">{tenancy.property_address || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Move-in</p>
              <p className="text-slate-700">{formatDate(tenancy.move_in_date)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Move-out</p>
              <p className="text-slate-700">{formatDate(tenancy.move_out_date)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Rent</p>
              <p className="text-slate-700">${tenancy.rent_amount?.toLocaleString() || '—'}/mo</p>
            </div>
          </div>

          {/* Payment Summary */}
          {reports.length > 0 && (
            <div className="flex items-center gap-4 py-2 border-t border-slate-200 text-xs">
              <span className="text-slate-400">Payments:</span>
              <span className="text-emerald-600">{paymentSummary.onTime} on-time</span>
              <span className="text-amber-600">{paymentSummary.late} late</span>
              <span className="text-red-600">{paymentSummary.missed} missed</span>
            </div>
          )}

          {/* Actions - Text links style */}
          <div className="flex items-center gap-4 pt-2 border-t border-slate-200">
            <a 
              href={`/landlord/reports/${tenancy.id}/new`}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1 hover:underline"
            >
              <Plus className="w-3.5 h-3.5" />
              Add entry
            </a>
            {tenancy.status === 'active' && (
              <button
                onClick={() => onEndTenancy(tenancy)}
                className="text-xs text-slate-500 hover:text-slate-700 font-medium inline-flex items-center gap-1 hover:underline"
              >
                <LogOut className="w-3.5 h-3.5" />
                End tenancy
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Empty State
function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
        <FileText className="w-6 h-6 text-slate-400" />
      </div>
      <h3 className="text-base font-medium text-slate-900 mb-1">No tenancies yet</h3>
      <p className="text-sm text-slate-500 max-w-sm mx-auto mb-4">
        Add a tenancy to start submitting behaviour reports.
      </p>
      <a href="/landlord/tenants/new" className="text-sm text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1">
        <Plus className="w-4 h-4" />
        Add tenancy
      </a>
    </div>
  );
}

export default function ReportsPage() {
  const [tenancies, setTenancies] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [reports, setReports] = useState([]);
  const [properties, setProperties] = useState([]);
  const [openTenantId, setOpenTenantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useUrlParam('status');
  const [propertyFilter, setPropertyFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setSubscriptionRequired(false);
      const [tenanciesRes, tenantsRes, reportsRes, propertiesRes] = await Promise.all([
        api.get('/tenancies'),
        api.get('/tenants'),
        api.get('/reports?limit=500'),
        api.get('/properties?limit=500')
      ]);
      setTenancies(tenanciesRes.data.items || []);
      setTenants(tenantsRes.data.items || []);
      setReports(reportsRes.data.items || []);
      setProperties(propertiesRes.data.items || []);
    } catch (err) {
      // Handle 402 Payment Required - show subscription gate
      if (err.response?.status === 402) {
        setSubscriptionRequired(true);
        setSubscriptionMessage(err.response?.data?.detail?.message || 'An active subscription is required to view your reports.');
        return;
      }
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const getTenant = (tenantId) => tenants.find(t => t.id === tenantId);
  const getTenancyReports = (tenancyId) => reports.filter(r => r.tenancy_id === tenancyId);

  const filteredTenancies = tenancies.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (propertyFilter !== 'all' && t.property_id !== propertyFilter) return false;
    
    if (searchTerm) {
      const tenant = getTenant(t.tenant_id);
      const search = searchTerm.toLowerCase();
      const matchesName = tenant?.full_name?.toLowerCase().includes(search);
      const matchesId = tenant?.tenant_unique_id?.includes(search);
      const matchesAddress = t.property_address?.toLowerCase().includes(search);
      if (!matchesName && !matchesId && !matchesAddress) return false;
    }
    
    return true;
  });

  const REPORT_COLUMNS = [
    { key: 'tenant', label: 'Tenant', span: 3 },
    { key: 'property', label: 'Property', span: 2 },
    { key: 'rent', label: 'Rent', span: 2 },
    { key: 'movein', label: 'Move-in', span: 2 },
    { key: 'status', label: 'Status', span: 2 },
    { key: 'open', label: '', span: 1, sortable: false },
  ];
  const { sorted: sortedTenancies, sort, toggle: toggleSort } = useSort(filteredTenancies, {
    tenant: (t) => getTenant(t.tenant_id)?.full_name,
    property: (t) => t.property_address,
    rent: (t) => t.rent_amount ?? null,
    movein: (t) => (t.start_date ? new Date(t.start_date).getTime() : null),
    status: (t) => (STATUS_CONFIG[t.status] || STATUS_CONFIG.active).label,
  });
  const handleExport = () => exportCsv('tenantra-reports.csv', sortedTenancies, [
    { label: 'Tenant', value: (t) => getTenant(t.tenant_id)?.full_name || '' },
    { label: 'Tenant ID', value: (t) => getTenant(t.tenant_id)?.tenant_unique_id || '' },
    { label: 'Property', value: (t) => t.property_address || '' },
    { label: 'Rent', value: (t) => t.rent_amount ?? '' },
    { label: 'Move-in', value: (t) => t.start_date || '' },
    { label: 'Move-out', value: (t) => t.end_date || '' },
    { label: 'Status', value: (t) => (STATUS_CONFIG[t.status] || STATUS_CONFIG.active).label },
    { label: 'Reports filed', value: (t) => getTenancyReports(t.id).length },
  ]);

  const stats = {
    total: tenancies.length,
    active: tenancies.filter(t => t.status === 'active').length,
    pendingConsent: tenancies.filter(t => t.status === 'pending_consent').length,
    ended: tenancies.filter(t => t.status === 'ended').length,
    reportsThisMonth: reports.filter(r => {
      const date = new Date(r.created_at);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length
  };

  const handleEndTenancy = (tenancy) => {
    navigate(`/landlord/tenants/${tenancy.tenant_id}/end?tenancy=${tenancy.id}`);
  };

  // Show subscription gate if subscription is required
  if (subscriptionRequired) {
    return <SubscriptionGate message={subscriptionMessage} />;
  }

  return (
    <div className="space-y-5">
      {/* Header - Microsoft style */}
      <div>
        <h1 className="text-sm font-semibold text-slate-800">Reports</h1>
        <p className="text-xs text-slate-400">Manage tenancies and submit behaviour reports</p>
      </div>

      {/* Stats Row - Compact inline */}
      {tenancies.length > 0 && (
        <div className="flex items-center gap-5 py-2 border-b border-slate-200 flex-wrap text-xs">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-emerald-600">{stats.active}</span>
            <span className="text-xs text-slate-400">Active</span>
          </div>
          <div className="w-px h-4 bg-slate-200" />
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-teal-600">{stats.reportsThisMonth}</span>
            <span className="text-xs text-slate-400">This month</span>
          </div>
          <div className="w-px h-4 bg-slate-200" />
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-amber-600">{stats.pendingConsent}</span>
            <span className="text-xs text-slate-400">Pending</span>
          </div>
          <div className="w-px h-4 bg-slate-200" />
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-slate-400">{stats.ended}</span>
            <span className="text-xs text-slate-400">Ended</span>
          </div>
        </div>
      )}

      {/* Actions Row */}
      <div className="flex items-center gap-4">
        <button 
          onClick={fetchData}
          className="text-sm text-slate-500 hover:text-slate-700 font-medium inline-flex items-center gap-1.5 hover:underline"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
        <ExportButton onClick={handleExport} disabled={loading || sortedTenancies.length === 0} testId="reports-export-btn" />
      </div>

      {/* Filters - Compact row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 pl-9 text-sm bg-white border-slate-200 placeholder:text-slate-400"
          />
        </div>
        
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-3 pr-7 bg-white border border-slate-200 rounded-md text-slate-600 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 appearance-none cursor-pointer"
          >
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="pending_consent">Pending</option>
            <option value="ended">Ended</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
        
        <div className="relative">
          <select
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            className="h-9 px-3 pr-7 bg-white border border-slate-200 rounded-md text-slate-600 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 appearance-none cursor-pointer"
          >
            <option value="all">All properties</option>
            {properties.map(p => (
              <option key={p.id} value={p.id}>
                {p.address?.street || 'Unknown'}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Tenancies List */}
      {loading ? (
        <TableSkeleton rows={8} spans={[3, 2, 2, 2, 2, 1]} />
      ) : filteredTenancies.length === 0 ? (
        searchTerm || statusFilter !== 'all' || propertyFilter !== 'all' ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
            <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-medium text-slate-800 mb-1">No results</h3>
            <p className="text-sm text-slate-500">Try adjusting your filters</p>
          </div>
        ) : (
          <EmptyState />
        )
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg">
          <SortableHeader columns={REPORT_COLUMNS} sort={sort} onSort={toggleSort} />
          {sortedTenancies.map(tenancy => (
            <TenancyRow
              key={tenancy.id}
              tenancy={tenancy}
              tenant={getTenant(tenancy.tenant_id)}
              reports={getTenancyReports(tenancy.id)}
              onEndTenancy={handleEndTenancy}
              onOpen={setOpenTenantId}
            />
          ))}
        </div>
      )}
      <TenantPanel tenantId={openTenantId} onClose={() => setOpenTenantId(null)} />
    </div>
  );
}
